import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Divider,
  Paper,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  EventNote as EventNoteIcon,
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Psychology as PsychologyIcon,
  DateRange as DateRangeIcon,
  LocalHospital as LocalHospitalIcon,
  Visibility as VisibilityIcon,
  Today as TodayIcon,
  Description as DocumentIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { format, compareAsc, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import historiaService from '../../services/historiaService';
import { useNotification } from '../../contexts/NotificationContext';

const HistoriaTimeline = ({ historiaId }) => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(true);
  const [timelineEvents, setTimelineEvents] = useState([]);

  // Cargar eventos para la timeline
  useEffect(() => {
    const fetchTimelineData = async () => {
      setLoading(true);
      try {
        // Cargar historia
        const historiaResponse = await historiaService.getHistoria(historiaId);
        const historia = historiaResponse.data;
        
        // Cargar sesiones
        const sesionesResponse = await historiaService.getSesiones(historiaId);
        const sesiones = sesionesResponse.data || [];
        
        // Cargar documentos
        const documentosResponse = await historiaService.getDocumentos(historiaId);
        const documentos = documentosResponse.data || [];
        
        // Crear eventos para la historia
        let events = [
          {
            id: `historia-${historia.id}`,
            tipo: 'historia',
            subtipo: 'creacion',
            titulo: 'Inicio de Historia Clínica',
            fecha: historia.fecha_creacion,
            color: 'primary.main',
            icono: <LocalHospitalIcon color="primary" />,
            data: historia
          }
        ];
        
        // Eventos de estado
        if (historia.fecha_finalizacion) {
          events.push({
            id: `historia-fin-${historia.id}`,
            tipo: 'historia',
            subtipo: 'finalizacion',
            titulo: 'Finalización de Historia Clínica',
            fecha: historia.fecha_finalizacion,
            color: 'success.main',
            icono: <CheckCircleIcon color="success" />,
            data: historia
          });
        }
        
        // Eventos de sesiones
        sesiones.forEach(sesion => {
          let color, icon;
          
          switch (sesion.estado) {
            case 'Realizada':
              color = 'success.main';
              icon = <CheckCircleIcon color="success" />;
              break;
            case 'Programada':
              color = 'primary.main';
              icon = <DateRangeIcon color="primary" />;
              break;
            case 'Cancelada':
              color = 'error.main';
              icon = <CancelIcon color="error" />;
              break;
            case 'No asistió':
              color = 'warning.main';
              icon = <CancelIcon color="warning" />;
              break;
            default:
              color = 'grey.500';
              icon = <EventNoteIcon />;
          }
          
          events.push({
            id: `sesion-${sesion.id}`,
            tipo: 'sesion',
            subtipo: sesion.estado.toLowerCase(),
            titulo: `Sesión ${sesion.estado}`,
            descripcion: `Sesión con ${sesion.especialista?.usuario?.nombre} ${sesion.especialista?.usuario?.apellidos}`,
            fecha: sesion.fecha,
            hora: sesion.hora_inicio,
            color,
            icono: icon,
            data: sesion
          });
        });
        
        
        // Eventos de documentos
        documentos.forEach(documento => {
          events.push({
            id: `documento-${documento.id}`,
            tipo: 'documento',
            titulo: 'Documento Adjuntado',
            descripcion: documento.nombre,
            fecha: documento.fecha_subida,
            color: 'secondary.main',
            icono: <DocumentIcon color="secondary" />,
            data: documento
          });
        });
        
        // Ordenar eventos por fecha
        events.sort((a, b) => {
          const dateA = parseISO(`${a.fecha}${a.hora ? 'T' + a.hora : 'T00:00:00'}`);
          const dateB = parseISO(`${b.fecha}${b.hora ? 'T' + b.hora : 'T00:00:00'}`);
          return compareAsc(dateB, dateA); // Orden descendente (más reciente primero)
        });
        
        setTimelineEvents(events);
      } catch (error) {
        console.error('Error al cargar datos para la timeline:', error);
        showNotification('Error al cargar el historial de eventos', 'error');
      } finally {
        setLoading(false);
      }
    };
    
    if (historiaId) {
      fetchTimelineData();
    }
  }, [historiaId, showNotification]);

  // Formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'dd MMM yyyy', { locale: es });
    } catch (error) {
      return dateString;
    }
  };

  // Formatear hora
  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      const [hours, minutes] = timeString.split(':');
      const date = new Date();
      date.setHours(parseInt(hours, 10));
      date.setMinutes(parseInt(minutes, 10));
      return format(date, 'h:mm a', { locale: es });
    } catch (error) {
      return timeString;
    }
  };

  // Navegar a detalles del elemento
  const handleViewDetails = (event) => {
    switch (event.tipo) {
      case 'sesion':
        // Navegar a detalle de sesión (si existe la ruta)
        showNotification('Funcionalidad de detalle de sesión en desarrollo', 'info');
        break;
      case 'evaluacion':
        // Navegar a detalle de evaluación
        navigate(`/evaluaciones/${event.data.id}`);
        break;
      case 'documento':
        // Mostrar documento
        showNotification('Funcionalidad de visualización de documento en desarrollo', 'info');
        break;
      default:
        break;
    }
  };

  // Obtener etiqueta del tipo de evento
  const getEventTypeLabel = (event) => {
    switch (event.tipo) {
      case 'historia':
        return event.subtipo === 'creacion' ? 'Inicio Tratamiento' : 'Fin Tratamiento';
      case 'sesion':
        return 'Sesión';
      case 'evaluacion':
        return 'Evaluación';
      case 'documento':
        return 'Documento';
      default:
        return 'Evento';
    }
  };

  // Obtener color del evento
  const getEventColor = (event) => {
    switch (event.tipo) {
      case 'historia':
        return event.subtipo === 'creacion' ? 'primary' : 'success';
      case 'sesion':
        switch (event.subtipo) {
          case 'realizada':
            return 'success';
          case 'programada':
            return 'primary';
          case 'cancelada':
            return 'error';
          case 'no asistió':
            return 'warning';
          default:
            return 'default';
        }
      case 'evaluacion':
        return 'info';
      case 'documento':
        return 'secondary';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Línea de Tiempo
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        {timelineEvents.length === 0 ? (
          <Typography variant="body1" color="textSecondary" align="center">
            No hay eventos registrados en esta historia clínica.
          </Typography>
        ) : (
          <Box sx={{ position: 'relative' }}>
            {/* Línea vertical */}
            <Box 
              sx={{
                position: 'absolute',
                left: 20,
                top: 0,
                bottom: 0,
                width: 2,
                backgroundColor: 'divider',
                zIndex: 0
              }}
            />
            
            {/* Eventos */}
            {timelineEvents.map((event, index) => (
              <Box 
                key={event.id}
                sx={{
                  display: 'flex',
                  mb: index < timelineEvents.length - 1 ? 3 : 0,
                  position: 'relative'
                }}
              >
                {/* Indicador (círculo) en la línea */}
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: 'background.paper',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 1,
                    borderColor: 'divider',
                    zIndex: 1,
                    boxShadow: 1
                  }}
                >
                  {event.icono}
                </Box>
                
                {/* Contenido del evento */}
                <Paper 
                  sx={{ 
                    ml: 2, 
                    p: 2, 
                    flexGrow: 1,
                    borderLeft: 3,
                    borderColor: event.color
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Box display="flex" alignItems="center" mb={0.5}>
                        <Chip 
                          label={getEventTypeLabel(event)} 
                          size="small" 
                          color={getEventColor(event)}
                          sx={{ mr: 1 }}
                        />
                        <Typography variant="subtitle1">
                          {event.titulo}
                        </Typography>
                      </Box>
                      
                      <Typography variant="body2" color="textSecondary">
                        {formatDate(event.fecha)}
                        {event.hora && ` · ${formatTime(event.hora)}`}
                      </Typography>
                    </Box>
                    
                    {/* Botón de acción según tipo */}
                    {(event.tipo === 'sesion' || event.tipo === 'evaluacion' || event.tipo === 'documento') && (
                      <Tooltip title="Ver detalles">
                        <IconButton 
                          size="small" 
                          onClick={() => handleViewDetails(event)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  
                  {event.descripcion && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {event.descripcion}
                    </Typography>
                  )}
                </Paper>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default HistoriaTimeline;